import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_NAMES } from '../../lib/api';

/**
 * Sdílená SEO komponenta pro veřejné stránky.
 * Nastavuje <title>, meta description, Open Graph, Twitter Card a canonical URL.
 *
 * Props:
 *  - title: string - konkrétní název stránky (např. "Aktuality")
 *  - description: string - popis stránky pro vyhledávače (max ~160 znaků)
 *  - siteKey: 'ms' | 'ds' - určuje větev pro title suffix a keywords
 *  - image: string - absolutní URL obrázku pro OG (volitelně)
 *  - keywords: string[] - další klíčová slova nad rámec výchozích
 *  - type: 'website' | 'article' (default 'website')
 *  - jsonLd: object | array - libovolný Schema.org JSON-LD navíc
 *  - noindex: boolean - pokud true, zakáže indexaci stránky
 */
export const SEO = ({
  title,
  description,
  siteKey,
  image,
  keywords = [],
  type = 'website',
  jsonLd = null,
  noindex = false,
}) => {
  const siteBrand = siteKey ? SITE_NAMES[siteKey] : 'MŠ Nová Role';
  const fullTitle = title
    ? `${title} | ${siteBrand}`
    : `${siteBrand} - Mateřská škola Nová Role`;

  const desc = description ||
    'Mateřská škola Cestička v Nové Roli a Dětská skupina Chaloupka na vršku. Rodinné prostředí, individuální přístup, kvalitní vzdělávání pro předškolní děti v Karlovarském kraji.';

  const defaultKeywords = [
    'mateřská škola Nová Role',
    'MŠ Nová Role',
    'MŠ Cestička',
    'dětská skupina Nová Role',
    'Chaloupka na vršku',
    'školka Karlovarský kraj',
    'předškolní vzdělávání',
    'zápis do školky',
  ];
  const allKeywords = [...new Set([...defaultKeywords, ...keywords])].join(', ');

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const ogImage = image || (typeof window !== 'undefined'
    ? `${window.location.origin}/favicon.ico`
    : '');

  return (
    <Helmet>
      <html lang="cs" />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={allKeywords} />
      <meta name="author" content={siteBrand} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      <link rel="canonical" href={currentUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteBrand} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:locale" content="cs_CZ" />
      {ogImage && <meta property="og:image" content={ogImage} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {/* JSON-LD strukturovaná data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

/**
 * Připraví Schema.org JSON-LD pro instituci (Preschool / EducationalOrganization).
 */
export const buildOrganizationJsonLd = (siteKey) => {
  const isMs = siteKey === 'ms';
  const name = isMs ? 'MŠ Cestička' : 'Chaloupka na vršku';
  const url = typeof window !== 'undefined'
    ? `${window.location.origin}/${siteKey}`
    : `https://novy.msnovarole.cz/${siteKey}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'Preschool',
    name,
    url,
    address: {
      '@type': 'PostalAddress',
      streetAddress: isMs ? 'Nová Role 123' : 'Horní 456',
      addressLocality: 'Nová Role',
      postalCode: '362 25',
      addressCountry: 'CZ',
    },
    areaServed: {
      '@type': 'AdministrativeArea',
      name: 'Karlovarský kraj',
    },
  };
};

export default SEO;
